import { useState } from "react";
import { canDo } from "@shared/roles";
import { useClients, useCreateClient, useUpdateClient, useDeleteClient } from "@/hooks/use-clients";
import { useCommercials, useTechnicians } from "@/hooks/use-profiles";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchSelect } from "@/components/search-select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AddressAutocomplete from "@/components/address-autocomplete";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus, Search, Users, Mail, Phone, Pencil, Eye, Trash2,
  Loader2, Send, Upload, Clock, BadgeCheck, ClipboardList, Megaphone,
} from "lucide-react";
import { CopyButton } from "@/components/CopyButton";
import { useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import type { Client } from "@shared/schema";
import { ClientDetailContent } from "./detail-content";
import { ClientAvatar, contractLabels, fetchLogoForDomain } from "./client-shared";
import { setCameFromList } from "./navigation-state";

const emptyForm = {
  clientNumber: "",
  companyName: "",
  nomCommercial: "",
  siren: "",
  sector: "",
  groupName: "",
  contractType: "" as string,
  leaser: "",
  address: "",
  contactCivility: "" as string,
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  environmentType: "",
  commercialId: "",
  contractStartDate: "",
  contractEndDate: "",
  installerTechnicianId: "",
  maintenanceContract: false,
  logoUrl: "",
  website: "",
  siteInstallation: "",
  notes: "",
};

export default function ClientList() {
  const { profile } = useAuth();
  const { data: clients, isLoading } = useClients();
  const { data: commercialsList } = useCommercials();
  const { data: techniciansList } = useTechnicians();
  const [search, setSearch] = useState(
    () => new URLSearchParams(window.location.search).get("search") || ""
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createMutation = useCreateClient();
  const updateMutation = useUpdateClient();
  const deleteMutation = useDeleteClient();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [, setLocation] = useLocation();

  const canEdit = canDo(profile?.accessRole) || profile?.role === "planification" || profile?.role === "commercial";
  const isAdmin = canDo(profile?.accessRole);
  const commercials = commercialsList || [];
  const technicians = techniciansList || [];

  const [declarationOpen, setDeclarationOpen] = useState(false);
  const [declarationCommercialId, setDeclarationCommercialId] = useState("");
  const [declarationSending, setDeclarationSending] = useState(false);

  const handleDeclarationInit = async () => {
    if (!declarationCommercialId) return;
    setDeclarationSending(true);
    try {
      const res = await authFetch("/api/clients/declaration/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ commercialId: declarationCommercialId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Erreur inconnue");
      setDeclarationOpen(false);
      setDeclarationCommercialId("");
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: "Lien envoyé !", description: "Le commercial a reçu le lien de déclaration." });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    } finally {
      setDeclarationSending(false);
    }
  };

  const estNouveau = (c: any) => {
    if (!c.venteDeclareAt) return false;
    return new Date(c.venteDeclareAt).getTime() > Date.now() - 30 * 24 * 60 * 60 * 1000;
  };

  const enAttenteDeclaration = (c: any) =>
    c.declarationStatut === "lien_envoye" && !c.venteDeclareAt;

  const [filterAttenteDeclaration, setFilterAttenteDeclaration] = useState(
    () => new URLSearchParams(window.location.search).get("attenteDeclaration") === "1"
  );

  const updateQP = (key: string, value: string | null) => {
    const p = new URLSearchParams(window.location.search);
    if (value) p.set(key, value); else p.delete(key);
    const qs = p.toString();
    window.history.replaceState(
      null,
      "",
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname
    );
  };

  const [formData, setFormData] = useState({ ...emptyForm, commercialId: profile?.id || "" });
  const [editData, setEditData] = useState({ ...emptyForm });

  const filteredClients = clients?.filter((client: Client) => {
    const matchSearch =
      client.companyName.toLowerCase().includes(search.toLowerCase()) ||
      client.contactName.toLowerCase().includes(search.toLowerCase()) ||
      (client.clientNumber && client.clientNumber.toLowerCase().includes(search.toLowerCase()));
    const matchFilter = !filterAttenteDeclaration || enAttenteDeclaration(client);
    return matchSearch && matchFilter;
  })?.slice().sort((a: Client, b: Client) => {
    const na = (a as any).numeroClient;
    const nb = (b as any).numeroClient;
    if (na != null && nb != null) return na - nb;
    if (na != null) return -1;
    if (nb != null) return 1;
    return a.companyName.localeCompare(b.companyName, "fr");
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({
        ...formData,
        clientNumber: formData.clientNumber || null,
        commercialId: formData.commercialId || profile!.id,
        siren: formData.siren || null,
        sector: formData.sector || null,
        groupName: formData.groupName || null,
        contractType: (formData.contractType as "achat" | "location" | "poc") || null,
        leaser: formData.leaser || null,
        contactCivility: (formData.contactCivility as "M." | "Mme") || null,
        environmentType: formData.environmentType || null,
        logoUrl: formData.logoUrl || null,
        website: formData.website || null,
        notes: formData.notes || null,
        installerTechnicianId: formData.installerTechnicianId || null,
        contractStartDate: formData.contractStartDate || null,
        contractEndDate: formData.contractEndDate || null,
      } as any);
      setCreateOpen(false);
      toast({ title: "Client ajouté", description: "Le client a été créé avec succès" });
      setFormData({ ...emptyForm, commercialId: profile?.id || "" });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur", description: e?.message || "Impossible de créer le client" });
    }
  };

  const openEdit = (client: Client) => {
    setSelectedClient(client);
    setEditData({
      clientNumber: client.clientNumber || "",
      companyName: client.companyName,
      nomCommercial: (client as any).nomCommercial || "",
      siren: client.siren || "",
      sector: client.sector || "",
      groupName: client.groupName || "",
      contractType: client.contractType || "",
      leaser: client.leaser || "",
      address: client.address,
      contactCivility: client.contactCivility || "",
      contactName: client.contactName,
      contactPhone: client.contactPhone,
      contactEmail: client.contactEmail,
      environmentType: client.environmentType || "",
      commercialId: client.commercialId,
      contractStartDate: client.contractStartDate || "",
      contractEndDate: client.contractEndDate || "",
      installerTechnicianId: client.installerTechnicianId || "",
      maintenanceContract: client.maintenanceContract ?? false,
      logoUrl: (client as any).logoUrl || "",
      website: (client as any).website || "",
      siteInstallation: (client as any).siteInstallation || "",
      notes: client.notes || "",
    });
    setEditOpen(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient) return;
    try {
      await updateMutation.mutateAsync({
        id: selectedClient.id,
        ...editData,
        clientNumber: editData.clientNumber || null,
        siren: editData.siren || null,
        sector: editData.sector || null,
        groupName: editData.groupName || null,
        contractType: (editData.contractType as "achat" | "location" | "poc") || null,
        leaser: editData.leaser || null,
        contactCivility: (editData.contactCivility as "M." | "Mme") || null,
        environmentType: editData.environmentType || null,
        contractStartDate: editData.contractStartDate || null,
        contractEndDate: editData.contractEndDate || null,
        installerTechnicianId: editData.installerTechnicianId || null,
        maintenanceContract: editData.maintenanceContract,
        logoUrl: editData.logoUrl || null,
        website: editData.website || null,
        siteInstallation: (editData as any).siteInstallation || null,
        notes: editData.notes || null,
      } as any);
      setEditOpen(false);
      toast({ title: "Client modifié", description: "Les modifications ont été enregistrées" });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de modifier le client" });
    }
  };

  const openDetail = (client: Client) => {
    setCameFromList();
    setLocation(`/clients/${client.id}`);
  };

  const renderForm = (
    data: typeof formData,
    setData: typeof setFormData,
    onSubmit: (e: React.FormEvent) => void,
    isPending: boolean,
    submitLabel: string
  ) => (
    <form onSubmit={onSubmit} className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>N° client</Label>
          <Input data-testid="input-client-number" value={data.clientNumber} onChange={e => setData({ ...data, clientNumber: e.target.value })} placeholder="Ex: CLI-001" />
        </div>
        <div className="space-y-2">
          <Label>Raison sociale *</Label>
          <Input data-testid="input-company-name" required value={data.companyName} onChange={e => setData({ ...data, companyName: e.target.value })} placeholder="Ex: Restaurant Le Gourmet SAS" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Label>Nom commercial</Label>
            <span
              title="Nom utilisé au quotidien (ex: Evotion pour la société EVEOZ SAS)"
              className="w-3.5 h-3.5 rounded-full bg-muted text-muted-foreground text-[9px] flex items-center justify-center cursor-help border border-border font-bold flex-shrink-0"
            >?</span>
          </div>
          <Input
            data-testid="input-client-nom-commercial"
            value={(data as any).nomCommercial || ""}
            onChange={e => setData({ ...data, nomCommercial: e.target.value } as any)}
            placeholder="Ex: Chez Pierre"
          />
        </div>
        <div className="space-y-2">
          <Label>SIREN</Label>
          <Input value={data.siren} onChange={e => setData({ ...data, siren: e.target.value })} placeholder="123 456 789" />
        </div>
        <div className="space-y-2">
          <Label>Secteur d'activité</Label>
          <Input value={data.sector} onChange={e => setData({ ...data, sector: e.target.value })} placeholder="Restauration, Hôtellerie..." />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Groupe d'appartenance</Label>
          <Input value={data.groupName} onChange={e => setData({ ...data, groupName: e.target.value })} placeholder="Ex: Boulangerie Ange" />
        </div>
        <div className="space-y-2">
          <Label>Type de contrat</Label>
          <Select value={data.contractType} onValueChange={v => setData({ ...data, contractType: v })}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="achat">Achat</SelectItem>
              <SelectItem value="location">Location</SelectItem>
              <SelectItem value="poc">POC</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Leaser</Label>
          <Input value={data.leaser} onChange={e => setData({ ...data, leaser: e.target.value })} placeholder="Nom du leaser" />
        </div>
        <div className="space-y-2">
          <Label>Type d'environnement</Label>
          <Input data-testid="input-environment-type" value={data.environmentType} onChange={e => setData({ ...data, environmentType: e.target.value })} placeholder="Restaurant, Hôtel, Bureau..." />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Adresse de facturation *</Label>
        <AddressAutocomplete
          value={data.address}
          placeholder="Commencez à taper l'adresse…"
          required
          data-testid="input-address"
          onChange={(v) => setData({ ...data, address: v, latitude: null, longitude: null, geocodingStatus: "pending" } as any)}
          onSelect={(sel) => setData({
            ...data,
            address: sel.label,
            latitude: sel.latitude,
            longitude: sel.longitude,
            geocodingStatus: "ok",
            geocodedAddress: sel.label,
            geocodingScore: sel.score,
            geocodingLabel: sel.label,
            departementSiege: sel.departement,
          } as any)}
        />
      </div>
      <div className="space-y-2">
        <Label>Site à installer</Label>
        <Input data-testid="input-site-installation" value={(data as any).siteInstallation || ""} onChange={e => setData({ ...data, siteInstallation: e.target.value } as any)} placeholder="Adresse du site d'installation (si différente)" />
      </div>
      <div className="grid grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Civilité</Label>
          <Select value={data.contactCivility} onValueChange={v => setData({ ...data, contactCivility: v })}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="M.">M.</SelectItem>
              <SelectItem value="Mme">Mme</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Nom du contact *</Label>
          <Input data-testid="input-contact-name" required value={data.contactName} onChange={e => setData({ ...data, contactName: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Téléphone *</Label>
          <Input data-testid="input-contact-phone" required value={data.contactPhone} onChange={e => setData({ ...data, contactPhone: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Email *</Label>
          <div className="flex items-center gap-2">
            <Input
              data-testid="input-contact-email"
              type="email"
              required
              value={data.contactEmail}
              onChange={e => setData({ ...data, contactEmail: e.target.value })}
              onBlur={async (e) => {
                if ((data as any).website) return;
                const url = await fetchLogoForDomain(e.target.value);
                if (url && !data.logoUrl) setData(prev => ({ ...prev, logoUrl: url }));
              }}
            />
            <div className="relative group shrink-0">
              {data.logoUrl ? (
                <img src={data.logoUrl} alt="Logo" className="w-8 h-8 rounded object-contain border bg-white" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
              ) : (
                <div className="w-8 h-8 rounded border border-dashed border-muted-foreground/30 flex items-center justify-center text-muted-foreground/40">
                  <Upload className="w-3 h-3" />
                </div>
              )}
              {selectedClient && (
                <label className="absolute inset-0 cursor-pointer rounded opacity-0 group-hover:opacity-100 bg-black/40 flex items-center justify-center transition-opacity" title="Changer le logo">
                  <Upload className="w-3 h-3 text-white" />
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("file", file);
                    try {
                      const res = await authFetch(`/api/clients/${selectedClient.id}/logo`, { method: "POST", body: fd });
                      const updated = await res.json();
                      if (res.ok && updated.logoUrl) setData(prev => ({ ...prev, logoUrl: updated.logoUrl }));
                    } catch { /* ignore */ }
                  }} />
                </label>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Site internet</Label>
          <Input
            data-testid="input-website"
            type="url"
            value={(data as any).website || ""}
            onChange={e => setData({ ...data, website: e.target.value } as any)}
            placeholder="https://www.exemple.fr"
            onBlur={async (e) => {
              const url = await fetchLogoForDomain(e.target.value);
              if (url && !data.logoUrl) setData(prev => ({ ...prev, logoUrl: url }));
            }}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Commercial assigné</Label>
        <SearchSelect
          data-testid="select-commercial"
          value={data.commercialId || null}
          onValueChange={v => setData({ ...data, commercialId: v || "" })}
          options={commercials.map((c: any) => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))}
          placeholder="Sélectionner un commercial..."
          emptyText="Aucun commercial trouvé"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date de début de contrat</Label>
          <Input data-testid="input-contract-start" type="date" value={data.contractStartDate} onChange={e => setData({ ...data, contractStartDate: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Date de fin de contrat</Label>
          <Input data-testid="input-contract-end" type="date" value={data.contractEndDate} onChange={e => setData({ ...data, contractEndDate: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Technicien installateur</Label>
          <SearchSelect
            data-testid="select-installer-technician"
            value={data.installerTechnicianId || null}
            onValueChange={v => setData({ ...data, installerTechnicianId: v || "" })}
            options={technicians.map((t: any) => ({ value: t.id, label: `${t.firstName} ${t.lastName}` }))}
            placeholder="Sélectionner un technicien..."
            emptyText="Aucun technicien trouvé"
          />
        </div>
        <div className="flex items-end pb-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="maintenance-contract"
              data-testid="checkbox-maintenance"
              checked={data.maintenanceContract}
              onCheckedChange={(checked) => setData({ ...data, maintenanceContract: checked === true })}
            />
            <Label htmlFor="maintenance-contract" className="cursor-pointer">Contrat de maintenance</Label>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea data-testid="input-notes" value={data.notes || ""} onChange={e => setData({ ...data, notes: e.target.value })} placeholder="Notes internes..." />
      </div>
      <div className="pt-4 flex justify-end gap-2">
        <Button variant="outline" type="button" onClick={() => { setCreateOpen(false); setEditOpen(false); }}>Annuler</Button>
        <Button type="submit" disabled={isPending} data-testid="button-submit-client">
          {isPending ? "Enregistrement..." : submitLabel}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Clients</h1>
          <p className="text-muted-foreground mt-1">Gérer les clients</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Dialog open={declarationOpen} onOpenChange={setDeclarationOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="button-declarer-vente">
                  <Megaphone className="w-4 h-4 mr-2" />
                  Déclarer une vente
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[420px]">
                <DialogHeader>
                  <DialogTitle>Déclarer une vente</DialogTitle>
                  <DialogDescription>
                    Sélectionnez le commercial concerné. Un lien unique lui sera envoyé par email pour remplir la fiche client.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label>Commercial *</Label>
                    <SearchSelect
                      data-testid="select-declaration-commercial"
                      value={declarationCommercialId || null}
                      onValueChange={v => setDeclarationCommercialId(v || "")}
                      options={commercials.map((c: any) => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))}
                      placeholder="Sélectionner un commercial..."
                      emptyText="Aucun commercial trouvé"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" type="button" onClick={() => setDeclarationOpen(false)}>Annuler</Button>
                    <Button
                      type="button"
                      disabled={!declarationCommercialId || declarationSending}
                      onClick={handleDeclarationInit}
                      data-testid="button-declaration-send"
                    >
                      {declarationSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi…</> : <><Send className="w-4 h-4 mr-2" />Envoyer le lien</>}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="shadow-lg shadow-primary/20" data-testid="button-add-client">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau Client
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[700px]">
                <DialogHeader>
                  <DialogTitle>Nouveau Client</DialogTitle>
                  <DialogDescription>Ajouter un nouveau client au répertoire.</DialogDescription>
                </DialogHeader>
                {renderForm(formData, setFormData, handleSubmit, createMutation.isPending, "Créer le client")}
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-4 bg-card p-2 rounded-xl border shadow-sm flex-1 min-w-[220px]">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="input-search-clients"
              placeholder="Rechercher par société ou contact..."
              className="pl-9 border-none bg-transparent focus-visible:ring-0"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                updateQP("search", e.target.value || null);
              }}
            />
          </div>
        </div>
        <button
          data-testid="filter-attente-declaration"
          onClick={() => {
            const next = !filterAttenteDeclaration;
            setFilterAttenteDeclaration(next);
            updateQP("attenteDeclaration", next ? "1" : null);
          }}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
            filterAttenteDeclaration
              ? "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700"
              : "bg-card text-muted-foreground border shadow-sm hover:bg-secondary/40"
          }`}
        >
          <Clock className="w-3.5 h-3.5" />
          En attente de déclaration
          {filterAttenteDeclaration && clients && (
            <span className="ml-1 bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {clients.filter((c: any) => enAttenteDeclaration(c)).length}
            </span>
          )}
        </button>
      </div>

      <div className="rounded-xl border bg-card shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>N° client</TableHead>
              <TableHead>Société</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Contrat</TableHead>
              <TableHead>Environnement</TableHead>
              <TableHead className="text-right sticky right-0 z-20 bg-card before:content-[''] before:absolute before:inset-0 before:bg-secondary/40 before:pointer-events-none">
                <span className="relative z-10">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center h-32">Chargement...</TableCell>
              </TableRow>
            ) : filteredClients?.map((client: Client) => (
              <TableRow key={client.id} className="group hover:bg-secondary/20 transition-colors" data-testid={`row-client-${client.id}`}>
                <TableCell className="text-sm font-mono text-muted-foreground" data-testid={`text-client-number-${client.id}`}>
                  {(client as any).numeroClient != null
                    ? <span className="font-semibold text-foreground">{(client as any).numeroClient}</span>
                    : (client.clientNumber || "—")}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <ClientAvatar logoUrl={(client as any).logoUrl} companyName={(client as any).nomCommercial || client.companyName} size={40} />
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-foreground">{(client as any).nomCommercial || client.companyName}</span>
                        {estNouveau(client) && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold border border-green-200">
                            <BadgeCheck className="w-2.5 h-2.5" />Nouveau
                          </span>
                        )}
                        {(client as any).ficheACompleter && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-semibold border border-orange-200">
                            <ClipboardList className="w-2.5 h-2.5" />À compléter
                          </span>
                        )}
                        {enAttenteDeclaration(client) && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700"
                            title={
                              (client as any).declarationDerniereRelanceAt
                                ? `Dernière relance : ${new Date((client as any).declarationDerniereRelanceAt).toLocaleDateString("fr-FR")}`
                                : "Aucune relance encore envoyée"
                            }
                            data-testid={`badge-attente-declaration-${client.id}`}
                          >
                            <Clock className="w-2.5 h-2.5" />
                            Déclaration en attente
                            {((client as any).declarationRelancesCount ?? 0) > 0 && (
                              <span className="ml-0.5">· {(client as any).declarationRelancesCount}/5</span>
                            )}
                          </span>
                        )}
                      </div>
                      {(client as any).nomCommercial && client.companyName !== (client as any).nomCommercial && (
                        <div className="text-[10px] text-muted-foreground">{client.companyName}</div>
                      )}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <span className="truncate">{client.address}</span>
                        <CopyButton value={client.address || ""} ariaLabel="Copier l'adresse" size="sm" />
                      </div>
                      {client.groupName && <div className="text-xs text-muted-foreground">Groupe: {client.groupName}</div>}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="text-sm font-medium">{client.contactCivility} {client.contactName}</div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Mail className="w-3 h-3" />{client.contactEmail}
                      <CopyButton value={client.contactEmail || ""} ariaLabel="Copier l'email" size="sm" />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Phone className="w-3 h-3" />{client.contactPhone}
                      <CopyButton value={client.contactPhone || ""} ariaLabel="Copier le numéro" size="sm" />
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {client.contractType ? contractLabels[client.contractType] : "—"}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{client.environmentType || "—"}</TableCell>
                <TableCell className="text-right sticky right-0 z-20 bg-card before:content-[''] before:absolute before:inset-0 before:bg-secondary/20 before:opacity-0 group-hover:before:opacity-100 before:pointer-events-none">
                  <div className="relative z-10 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => openDetail(client)} data-testid={`button-view-client-${client.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(client)} data-testid={`button-edit-client-${client.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteConfirmId(client.id)} data-testid={`button-delete-client-${client.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && (!filteredClients || filteredClients.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucun client trouvé</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Modifier le client</DialogTitle>
            <DialogDescription>Modifier les informations du client.</DialogDescription>
          </DialogHeader>
          {renderForm(editData, setEditData, handleEdit, updateMutation.isPending, "Enregistrer")}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Supprimer le client</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ce client ? Cette action est irréversible. Les missions, tickets et dossiers SAV associés seront dissociés mais conservés.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} data-testid="button-cancel-delete-client">Annuler</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-client"
              onClick={() => {
                if (!deleteConfirmId) return;
                deleteMutation.mutate(deleteConfirmId, {
                  onSuccess: () => {
                    toast({ title: "Client supprimé" });
                    setDeleteConfirmId(null);
                  },
                  onError: (err: any) => {
                    toast({ title: "Erreur", description: err.message, variant: "destructive" });
                  },
                });
              }}
            >
              {deleteMutation.isPending ? "Suppression..." : "Supprimer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
